// components/CalendarFilters.js - Filtres pour la vue calendrier
import { Calendar, Filter, RefreshCw, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const CalendarFilters = ({
    filterByMonth,
    filterByCategory,
    filterByStatus,
    filterByPriority,
    availableMonths,
    availableCategories,
    availableStatuses,
    availablePriorities,
    onFilterMonthChange,
    onFilterCategoryChange,
    onFilterStatusChange,
    onFilterPriorityChange,
    onReset,
    onRefresh,
    resultCount,
    isLoading
}) => {
    const [isOpenMonth, setIsOpenMonth] = useState(false);
    const [isOpenCategory, setIsOpenCategory] = useState(false);
    const [isOpenStatus, setIsOpenStatus] = useState(false);
    const [isOpenPriority, setIsOpenPriority] = useState(false);

    const monthRef = useRef(null);
    const categoryRef = useRef(null);
    const statusRef = useRef(null);
    const priorityRef = useRef(null);

    // Options construites à partir des données réelles
    const monthOptions = [
        { value: 'all', label: 'Tous les mois' },
        ...availableMonths.map(month => {
            const [year, monthNum] = month.split('-');
            const monthNames = [
                'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
                'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
            ];
            return {
                value: month,
                label: `${monthNames[parseInt(monthNum) - 1]} ${year}`
            };
        })
    ];

    const categoryOptions = [
        { value: 'all', label: 'Toutes catégories' },
        ...availableCategories.map(category => ({ value: category, label: category }))
    ];

    const statusOptions = [
        { value: 'all', label: 'Tous les statuts' },
        ...availableStatuses.map(status => ({ value: status, label: status }))
    ];

    const priorityOptions = [
        { value: 'all', label: 'Toutes priorités' },
        ...availablePriorities.map(priority => {
            const priorityLabels = {
                'low': 'Basse',
                'medium': 'Moyenne',
                'high': 'Haute'
            };
            return { value: priority, label: priorityLabels[priority] || priority };
        })
    ];

    // Gestion des clics extérieurs pour fermer les dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (monthRef.current && !monthRef.current.contains(event.target)) {
                setIsOpenMonth(false);
            }
            if (categoryRef.current && !categoryRef.current.contains(event.target)) {
                setIsOpenCategory(false);
            }
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setIsOpenStatus(false);
            }
            if (priorityRef.current && !priorityRef.current.contains(event.target)) {
                setIsOpenPriority(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Vérifier si des filtres sont actifs
    const hasActiveFilters = filterByMonth !== 'all' ||
        filterByCategory !== 'all' ||
        filterByStatus !== 'all' ||
        filterByPriority !== 'all';

    return (
        <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm flex-shrink-0">
            {/* Filtres en ligne avec boutons d'action */}
            <div className="flex flex-wrap lg:flex-nowrap items-center gap-3">
                {/* Filtre par mois */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Mois</label>
                    <div className="relative w-40" ref={monthRef}>
                        <button
                            onClick={() => setIsOpenMonth(!isOpenMonth)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {monthOptions.find(opt => opt.value === filterByMonth)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenMonth ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenMonth && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {monthOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterMonthChange && onFilterMonthChange(option.value);
                                                setIsOpenMonth(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByMonth === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtre par catégorie */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Catégorie</label>
                    <div className="relative w-40" ref={categoryRef}>
                        <button
                            onClick={() => setIsOpenCategory(!isOpenCategory)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {categoryOptions.find(opt => opt.value === filterByCategory)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenCategory ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenCategory && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {categoryOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterCategoryChange && onFilterCategoryChange(option.value);
                                                setIsOpenCategory(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByCategory === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtre par statut */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Statut</label>
                    <div className="relative w-36" ref={statusRef}>
                        <button
                            onClick={() => setIsOpenStatus(!isOpenStatus)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {statusOptions.find(opt => opt.value === filterByStatus)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenStatus ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenStatus && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {statusOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterStatusChange && onFilterStatusChange(option.value);
                                                setIsOpenStatus(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByStatus === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Filtre par priorité */}
                <div className="flex items-center gap-2 min-w-0">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Priorité</label>
                    <div className="relative w-40" ref={priorityRef}>
                        <button
                            onClick={() => setIsOpenPriority(!isOpenPriority)}
                            disabled={isLoading}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#bed1d0]/20 focus:border-[#bed1d0] transition-all duration-200 cursor-pointer hover:border-[#7a9896] text-left flex items-center justify-between shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="text-gray-900 truncate">
                                {priorityOptions.find(opt => opt.value === filterByPriority)?.label || 'Sélectionner'}
                            </span>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1 ${isOpenPriority ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isOpenPriority && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-30 max-h-64 overflow-hidden">
                                <div className="max-h-64 overflow-y-auto">
                                    {priorityOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                onFilterPriorityChange && onFilterPriorityChange(option.value);
                                                setIsOpenPriority(false);
                                            }}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors duration-150 ${filterByPriority === option.value
                                                ? 'bg-[#bed1d0]/20 text-gray-800 font-medium'
                                                : 'text-gray-700 hover:bg-[#7a9896]/10 hover:text-gray-700'
                                                }`}
                                        >
                                            <span className="truncate block">{option.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bouton Réinitialiser */}
                <div className="flex-shrink-0 ml-auto">
                    <button
                        onClick={onReset}
                        disabled={isLoading}
                        className="w-10 h-[42px] flex items-center justify-center text-gray-600 hover:text-gray-700 hover:bg-[#7a9896]/10 border border-gray-200 hover:border-[#7a9896] rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Réinitialiser les filtres"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Résumé des filtres actifs */}
            {(hasActiveFilters || isLoading) && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                        {/* Indicateur de chargement */}
                        {isLoading && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Actualisation...
                            </div>
                        )}

                        {/* Filtres actifs sous forme de badges */}
                        {hasActiveFilters && !isLoading && (
                            <div className="flex flex-wrap gap-1">
                                {filterByMonth !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {monthOptions.find(opt => opt.value === filterByMonth)?.label}
                                        <button
                                            onClick={() => onFilterMonthChange && onFilterMonthChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {filterByCategory !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {filterByCategory}
                                        <button
                                            onClick={() => onFilterCategoryChange && onFilterCategoryChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {filterByStatus !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {filterByStatus}
                                        <button
                                            onClick={() => onFilterStatusChange && onFilterStatusChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {filterByPriority !== 'all' && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs bg-[#7a9896]/20 text-gray-700 rounded-full">
                                        {priorityOptions.find(opt => opt.value === filterByPriority)?.label}
                                        <button
                                            onClick={() => onFilterPriorityChange && onFilterPriorityChange('all')}
                                            className="ml-1 hover:text-gray-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarFilters;